import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity, Sparkles, AlertTriangle, AlertOctagon, CheckCircle2, XCircle,
  Cpu, Database, Globe, ListTree, Search, RefreshCw, X,
} from 'lucide-react';
import apiService from '../services/api';

export interface ObservabilityPanelProps {
  workspaceId?: string;        // when set, view is scoped to one workspace
  defaultWindow?: '1h' | '24h' | '7d';
}

type Category = 'llm_call' | 'mission_event' | 'http_request' | 'db_query_slow' | 'alert';
type StatusFilter = 'success' | 'failed' | 'cached' | 'info' | '';

const CATEGORIES: { id: Category | 'all'; label: string; icon: React.FC<any> }[] = [
  { id: 'all', label: 'Todos', icon: ListTree },
  { id: 'llm_call', label: 'LLM', icon: Sparkles },
  { id: 'mission_event', label: 'Misiones', icon: Cpu },
  { id: 'http_request', label: 'HTTP', icon: Globe },
  { id: 'db_query_slow', label: 'DB lenta', icon: Database },
];

const WINDOWS: { id: '1h' | '24h' | '7d'; label: string }[] = [
  { id: '1h', label: '1h' },
  { id: '24h', label: '24h' },
  { id: '7d', label: '7d' },
];

const fmtNum = (n: number) => new Intl.NumberFormat('es-MX').format(n);
const fmtMs = (n: number) => n < 1000 ? `${Math.round(n)}ms` : `${(n / 1000).toFixed(1)}s`;
const fmtPct = (n: number) => `${Math.round(n * 100)}%`;

const ObservabilityPanel = ({ workspaceId, defaultWindow = '24h' }: ObservabilityPanelProps) => {
  const [window, setWindow] = useState<'1h' | '24h' | '7d'>(defaultWindow);
  const [category, setCategory] = useState<Category | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [search, setSearch] = useState('');
  const [summary, setSummary] = useState<any>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<any | null>(null);
  const pollingTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const filterParams = useMemo(() => ({
    category: category === 'all' ? undefined : category,
    workspaceId,
    status: statusFilter || undefined,
    search: search.trim() || undefined,
  }), [category, workspaceId, statusFilter, search]);

  const refresh = useCallback(async (resetList: boolean = true) => {
    try {
      const [s, e, a] = await Promise.all([
        apiService.observabilitySummary(window, workspaceId),
        apiService.observabilityEvents({ ...filterParams, limit: 25 }),
        apiService.observabilityAlerts(window === '7d' ? '24h' : window, workspaceId),
      ]);
      setSummary(s);
      setAlerts(a.alerts || []);
      if (resetList) {
        setEvents(e.events || []);
        setHasMore(!!e.has_more);
        setNextCursor(e.next_cursor || null);
      }
    } catch {
      /* swallow — observability must not throw in UI */
    }
  }, [window, workspaceId, filterParams]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const e = await apiService.observabilityEvents({
        ...filterParams, limit: 25, cursor: nextCursor,
      });
      setEvents(prev => [...prev, ...(e.events || [])]);
      setHasMore(!!e.has_more);
      setNextCursor(e.next_cursor || null);
    } finally {
      setLoadingMore(false);
    }
  };

  // Initial + poll-every-2s
  useEffect(() => {
    refresh(true);
  }, [refresh]);

  useEffect(() => {
    if (pollingTimer.current) clearInterval(pollingTimer.current);
    pollingTimer.current = setInterval(() => { refresh(true); }, 2000);
    return () => { if (pollingTimer.current) clearInterval(pollingTimer.current); };
  }, [refresh]);

  const detRatio = summary?.deterministic_vs_ai?.deterministic_ratio ?? 0;
  const llmCalls = summary?.llm?.calls ?? 0;
  const llmFailed = summary?.llm?.failed ?? 0;
  const llmAvgMs = summary?.llm?.avg_latency_ms ?? 0;
  const httpReq = summary?.http?.requests ?? 0;
  const httpErr = summary?.http?.errors ?? 0;
  const totalTokens = summary?.llm?.total_tokens ?? 0;

  const allOk = llmFailed === 0 && httpErr === 0 && alerts.filter(a => a.level === 'error').length === 0;

  return (
    <div className="flex flex-col h-full" data-testid="observability-panel">
      {/* ============ FIXED HEADER ============ */}
      <div className="sticky top-0 z-10 bg-[#0A0B0D]/95 backdrop-blur border-b border-white/5">
        <div className="px-4 sm:px-6 py-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className={`w-2 h-2 rounded-full ${allOk ? 'bg-emerald-400' : 'bg-amber-400'} animate-pulse`} />
              <h2 className="text-white text-lg font-semibold tracking-tight" data-testid="observability-title">
                Observabilidad {workspaceId ? '· workspace' : '· global'}
              </h2>
              <span className="text-xs text-[#A1A1AA]" data-testid="observability-status-text">
                {allOk ? 'Todo funciona correctamente' : `${llmFailed + httpErr} fallos en ${window}`}
              </span>
            </div>
            <div className="flex items-center gap-1">
              {WINDOWS.map(w => (
                <button
                  key={w.id}
                  onClick={() => setWindow(w.id)}
                  data-testid={`obs-window-${w.id}`}
                  className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                    window === w.id ? 'bg-white text-black' : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'
                  }`}
                >{w.label}</button>
              ))}
              <button
                onClick={() => refresh(true)}
                data-testid="obs-refresh"
                className="ml-1 p-1.5 rounded-md bg-white/5 text-[#A1A1AA] hover:bg-white/10"
              ><RefreshCw className="w-3.5 h-3.5" /></button>
            </div>
          </div>

          {/* KPI strip — only top-level signals, never noise */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2" data-testid="obs-kpis">
            <Kpi testid="kpi-llm-calls" label="Llamadas LLM" value={fmtNum(llmCalls)} hint={llmFailed ? `${llmFailed} fallidas` : ''} negative={llmFailed > 0} />
            <Kpi testid="kpi-llm-tokens" label="Tokens totales" value={fmtNum(totalTokens)} hint="prompt+completion" />
            <Kpi testid="kpi-llm-latency" label="Latencia LLM" value={fmtMs(llmAvgMs)} hint="promedio" />
            <Kpi testid="kpi-http" label="HTTP" value={fmtNum(httpReq)} hint={httpErr ? `${httpErr} errores` : 'sin errores'} negative={httpErr > 0} />
            <Kpi testid="kpi-det-ratio" label="Determinista vs IA" value={fmtPct(detRatio)} hint={`${summary?.deterministic_vs_ai?.deterministic_events ?? 0} det / ${summary?.deterministic_vs_ai?.ai_calls ?? 0} IA`} />
          </div>

          {/* Alerts banner */}
          {alerts.length > 0 && (
            <div className="space-y-1.5" data-testid="obs-alerts">
              {alerts.map((a, i) => (
                <div
                  key={i}
                  data-testid={`obs-alert-${a.code}`}
                  className={`flex items-start gap-2 px-3 py-2 rounded-md border ${
                    a.level === 'error' ? 'bg-red-950/40 border-red-500/30 text-red-200' :
                    a.level === 'warning' ? 'bg-amber-950/40 border-amber-500/30 text-amber-200' :
                    'bg-white/5 border-white/10 text-[#A1A1AA]'
                  }`}
                >
                  {a.level === 'error' ? <AlertOctagon className="w-4 h-4 mt-0.5 shrink-0" /> :
                    a.level === 'warning' ? <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> :
                    <Activity className="w-4 h-4 mt-0.5 shrink-0" />}
                  <span className="text-xs leading-snug">{a.message}</span>
                </div>
              ))}
            </div>
          )}

          {/* Filters — category chips + search + status */}
          <div className="flex flex-wrap items-center gap-2" data-testid="obs-filters">
            {CATEGORIES.map(c => {
              const Icon = c.icon;
              const active = category === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setCategory(c.id)}
                  data-testid={`obs-cat-${c.id}`}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs transition-colors ${
                    active ? 'bg-white text-black' : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'
                  }`}
                >
                  <Icon className="w-3 h-3" /> {c.label}
                </button>
              );
            })}
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as StatusFilter)}
              data-testid="obs-status-select"
              className="text-xs bg-white/5 text-white border border-white/10 rounded-md px-2 py-1 focus:outline-none focus:border-white/30"
            >
              <option value="">Todos los estados</option>
              <option value="success">success</option>
              <option value="failed">failed</option>
              <option value="cached">cached</option>
              <option value="info">info</option>
            </select>
            <div className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-md px-2 py-1 flex-1 min-w-[180px] max-w-xs">
              <Search className="w-3.5 h-3.5 text-[#52525B]" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Buscar (path/modelo/módulo)"
                data-testid="obs-search"
                className="bg-transparent text-white text-xs flex-1 focus:outline-none placeholder-[#52525B]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ============ SCROLLABLE EVENT LIST ============ */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4" data-testid="obs-event-list">
        {events.length === 0 ? (
          <div className="text-center text-[#52525B] text-sm py-12">
            No hay eventos registrados en {window} con los filtros actuales.
          </div>
        ) : (
          <div className="space-y-1.5">
            {events.map(ev => (
              <EventRow key={ev.id} event={ev} onClick={() => setSelectedEvent(ev)} />
            ))}
            {hasMore && (
              <button
                onClick={loadMore}
                disabled={loadingMore}
                data-testid="obs-load-more"
                className="w-full mt-3 py-2 bg-white/5 hover:bg-white/10 text-[#A1A1AA] text-xs rounded-md disabled:opacity-50"
              >
                {loadingMore ? 'Cargando…' : `Cargar 25 más`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ============ DETAIL DRAWER ============ */}
      <AnimatePresence>
        {selectedEvent && (
          <EventDetailDrawer event={selectedEvent} onClose={() => setSelectedEvent(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};


const Kpi: React.FC<{ label: string; value: string; hint?: string; negative?: boolean; testid: string }> = ({ label, value, hint, negative, testid }) => (
  <div className="bg-white/[0.02] border border-white/5 rounded-lg px-3 py-2" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-[#52525B]">{label}</div>
    <div className={`text-lg font-mono ${negative ? 'text-red-400' : 'text-white'}`}>{value}</div>
    {hint && <div className="text-[10px] text-[#52525B]">{hint}</div>}
  </div>
);


const EventRow: React.FC<{ event: any; onClick: () => void }> = ({ event, onClick }) => {
  const ts = new Date(event.timestamp);
  const time = ts.toLocaleTimeString('es-MX', { hour12: false });
  const StatusIcon = event.status === 'failed' ? XCircle :
    event.status === 'cached' ? Database : CheckCircle2;
  const statusColor = event.status === 'failed' ? 'text-red-400' :
    event.status === 'cached' ? 'text-blue-400' :
    event.status === 'info' ? 'text-[#A1A1AA]' : 'text-emerald-400';
  const catColor =
    event.category === 'llm_call' ? 'bg-violet-500/15 text-violet-300 border-violet-500/30' :
    event.category === 'mission_event' ? 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30' :
    event.category === 'http_request' ? 'bg-white/5 text-[#A1A1AA] border-white/10' :
    'bg-amber-500/15 text-amber-300 border-amber-500/30';

  const p = event.payload || {};
  const summary = p.path || p.model || p.message || event.module || event.category;

  return (
    <button
      onClick={onClick}
      data-testid={`obs-event-row-${event.id}`}
      className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-md bg-white/[0.02] hover:bg-white/[0.05] border border-transparent hover:border-white/10 transition-colors"
    >
      <StatusIcon className={`w-3.5 h-3.5 shrink-0 ${statusColor}`} />
      <span className="text-[10px] font-mono text-[#52525B] w-16 shrink-0">{time}</span>
      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${catColor} font-mono shrink-0`}>
        {event.category.replace('_', ' ')}
      </span>
      <span className="text-xs text-[#A1A1AA] font-mono shrink-0 w-24 truncate">{event.module || '—'}</span>
      <span className="text-xs text-white flex-1 truncate">{summary}</span>
      {event.category === 'llm_call' && p.total_tokens != null && (
        <span
          className="text-[10px] text-violet-300/80 font-mono shrink-0"
          data-testid={`obs-event-tokens-${event.id}`}
          title={`prompt ${p.prompt_tokens ?? '?'} · completion ${p.completion_tokens ?? '?'}`}
        >
          {fmtNum(p.total_tokens)} tok
        </span>
      )}
      {event.duration_ms != null && (
        <span className="text-[10px] text-[#52525B] font-mono shrink-0">{fmtMs(event.duration_ms)}</span>
      )}
    </button>
  );
};


const EventDetailDrawer: React.FC<{ event: any; onClose: () => void }> = ({ event, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 bg-black/60 flex justify-end"
    onClick={onClose}
    data-testid="obs-event-detail"
  >
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 28 }}
      className="w-full sm:w-[420px] h-full bg-[#0A0B0D] border-l border-white/10 overflow-y-auto"
      onClick={e => e.stopPropagation()}
    >
      <div className="sticky top-0 bg-[#0A0B0D] border-b border-white/5 px-4 py-3 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#52525B]">Evento</div>
          <div className="text-white text-sm font-mono">{event.category}</div>
        </div>
        <button
          onClick={onClose}
          data-testid="obs-event-detail-close"
          className="p-1 rounded text-[#A1A1AA] hover:text-white hover:bg-white/10"
        ><X className="w-4 h-4" /></button>
      </div>
      <div className="p-4 space-y-3 text-xs">
        <DetailRow label="ID" value={event.id} />
        <DetailRow label="Timestamp" value={new Date(event.timestamp).toLocaleString('es-MX')} />
        <DetailRow label="Estado" value={event.status} />
        <DetailRow label="Módulo" value={event.module || '—'} />
        <DetailRow label="Duración" value={event.duration_ms != null ? fmtMs(event.duration_ms) : '—'} />

        {/* LLM-specific first-class rows — P3 of Sprint-Coordinator. */}
        {event.category === 'llm_call' && (
          <div
            className="space-y-3 border border-violet-500/20 bg-violet-500/[0.04] rounded-md p-3"
            data-testid="obs-event-detail-llm"
          >
            <div className="text-[10px] uppercase tracking-wider text-violet-300/80 font-mono">
              Llamada LLM
            </div>
            <DetailRow label="Modelo" value={event.payload?.model || '—'} />
            <DetailRow label="Provider" value={event.payload?.provider || '—'} />
            <DetailRow
              label="Tokens entrada"
              value={
                event.payload?.prompt_tokens != null
                  ? fmtNum(event.payload.prompt_tokens)
                  : '—'
              }
            />
            <DetailRow
              label="Tokens salida"
              value={
                event.payload?.completion_tokens != null
                  ? fmtNum(event.payload.completion_tokens)
                  : '—'
              }
            />
            <DetailRow
              label="Tokens totales"
              value={
                event.payload?.total_tokens != null
                  ? fmtNum(event.payload.total_tokens)
                  : '—'
              }
            />
            <DetailRow
              label="Streaming"
              value={event.payload?.streaming ? 'sí' : 'no'}
            />
            <DetailRow
              label="Cache hit"
              value={event.payload?.cache_hit ? 'sí' : 'no'}
            />
            {event.payload?.tokens_estimated && (
              <DetailRow label="Tokens" value="estimados (no exactos)" />
            )}
            {event.payload?.error && (
              <DetailRow label="Error" value={String(event.payload.error)} />
            )}
          </div>
        )}

        <DetailRow label="Workspace" value={event.workspace_id || '—'} />
        <DetailRow label="Mission" value={event.mission_id || '—'} />
        <DetailRow label="Usuario" value={event.user_id || '—'} />
        <DetailRow label="Interaction ID" value={event.interaction_id || '—'} />
        <DetailRow label="Session ID" value={event.session_id || '—'} />
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#52525B] mb-1">Payload</div>
          <pre className="bg-black/40 border border-white/5 rounded-md p-2 text-[10px] font-mono text-[#A1A1AA] overflow-x-auto">
            {JSON.stringify(event.payload, null, 2)}
          </pre>
        </div>
      </div>
    </motion.div>
  </motion.div>
);


const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between items-start gap-3">
    <span className="text-[10px] uppercase tracking-wider text-[#52525B] shrink-0">{label}</span>
    <span className="text-[#A1A1AA] font-mono text-right break-all">{value}</span>
  </div>
);


export default ObservabilityPanel;
